/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { UserManager, WebStorageStateStore, Log, User, SigninRequest } from 'oidc-client';
import { TLogin, IAuthentication } from './types';
import { IdaLoginScopeTypes as Scopes } from '@ida/ida-login-model';

const IDA_URL = runtimeConfig.idaDomain;
export default class Authentication implements IAuthentication {
  public static UserManager: UserManager;
  userManager: UserManager;

  constructor(login: TLogin) {
    const IDENTITY_CONFIG = {
      authority: login.authority,
      client_id: login.client_id,
      redirect_uri: `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }/signin-oidc`,
      silent_redirect_uri: `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }/silent-renew-oidc`,
      post_logout_redirect_uri: `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }/signout-callback-oidc`,
      automaticSilentRenew: true,
      loadUserInfo: true,
      response_type: 'code',
      scope: `openid profile ${Scopes.Roles} member-counseling-api.user`,
      filterProtocolClaims: true,
    };

    Authentication.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });

    this.userManager = Authentication.UserManager;

    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;

    this.userManager.events.addUserLoaded((user) => {
      console.log(user);
      if (user) {
        const { access_token, id_token, profile } = user;

        localStorage.setItem('access_token', access_token);
        localStorage.setItem('id_token', id_token);
        localStorage.setItem('username', profile.name ?? '');

        if (access_token) {
          const userData: { sub: string; role: string[] } = this.parseJwt(access_token);
          localStorage.setItem('userId', userData?.sub);
          localStorage.setItem('roles', JSON.stringify(userData?.role ?? []));
        }
      }

      if (window.location.href.indexOf('signin-oidc') !== -1) {
        this.navigateToScreen();
      }
    });

    this.userManager.events.addSilentRenewError((e) => {
      console.log('silent renew error', e.message);
    });

    this.userManager.events.addAccessTokenExpired(() => {
      console.log('token expired');
      this.signinSilent();
    });

    this.userManager.events.addUserSignedOut(() => {
      console.log('User signed out');
      this.userManager.clearStaleState();
      this.userManager.signinRedirect();
    });
  }

  getUsername = (): string | null => {
    const username = localStorage.getItem('username');
    return username;
  };

  signinRedirectCallback = (): void => {
    this.userManager.signinRedirectCallback().then(() => {
      ('');
    });
  };

  getUser = async (): Promise<User> => {
    let user = await this.userManager.getUser();
    if (!user) {
      user = await this.userManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };

  signinRedirect = (): void => {
    localStorage.setItem('redirectUri', window.location.pathname);
    this.userManager.signinRedirect({});
  };

  navigateToScreen = (): void => {
    const uri = localStorage.getItem('redirectUri');
    const redirectUri = uri || '/';
    window.location.replace(redirectUri);
  };

  isAuthenticated = (): boolean => {
    const accessToken = localStorage.getItem('access_token');
    return accessToken !== null && this.parseJwt(accessToken).exp > Date.now() / 1000;
  };

  hasRole = (requiredRoles: string[]): boolean => {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]');
    if (Array.isArray(roles)) {
      return roles && roles.some((roleName: string) => requiredRoles.includes(roleName));
    }
    // in case the role is single string, ex. "IDAMember"
    return requiredRoles.includes(roles);
  };

  signinSilent = async () => {
    try {
      const user = await this.userManager.signinSilent();
      console.log('signed in', user);
    } catch (err: any) {
      if (err.error === 'login_required') {
        this.signinRedirect();
      }
      console.log(err);
    }
  };

  signinSilentCallback = (): void => {
    this.userManager.signinSilentCallback();
  };

  createSigninRequest = (): Promise<SigninRequest> => {
    return this.userManager.createSigninRequest();
  };

  logout = () => {
    this.userManager.signoutRedirect({
      id_token_hint: localStorage.getItem('id_token'),
    });
    this.userManager.clearStaleState();
  };

  signoutRedirectCallback = async () => {
    await this.userManager.signoutRedirectCallback();
    localStorage.clear();
    window.location.replace(
      IDA_URL ??
        `${window.location.protocol}//${window.location.hostname}${
          window.location.port ? `:${window.location.port}` : ''
        }`,
    );
    this.userManager.clearStaleState();
  };
}
